import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiCalendar,
  FiCheckSquare,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiEdit2,
  FiList,
  FiPlus,
  FiSearch,
  FiX,
} from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import {
  createCrmTask,
  fetchCrmCompanies,
  fetchCrmContacts,
  fetchCrmConversations,
  fetchCrmLeads,
  fetchCrmTask,
  fetchCrmTasks,
  updateCrmTask,
} from "../crmApi";
import { useCrmCopy } from "../crmI18n";

const emptyForm = {
  title: "",
  description: "",
  task_type: "follow_up",
  priority: "normal",
  status: "open",
  due_at: "",
  reminder_at: "",
  conversation_id: "",
  lead_id: "",
  contact_customer_id: "",
  company_id: "",
};

const HOUR_START = 0;
const HOUR_END = 23;

function getContactLabel(contact) {
  if (!contact) return "";
  const fullName = contact?.full_name || [contact?.first_name, contact?.last_name].filter(Boolean).join(" ");
  return fullName || contact?.email || contact?.phone || "";
}

function getRelatedLabel(task, copy) {
  if (task?.lead?.title) return task.lead.title;
  if (task?.conversation?.subject) return task.conversation.subject;
  if (task?.company?.name) return task.company.name;
  return getContactLabel(task?.contact) || copy.common.noRecords;
}

function getTaskOwnerLabel(task) {
  const owner = task?.owner;
  if (!owner) return "";
  return owner.name || owner.email || (owner.id ? `User ${owner.id}` : "");
}

function readStoredCrmUser() {
  try {
    return JSON.parse(localStorage.getItem("palantir_user") || "{}");
  } catch (_err) {
    return {};
  }
}

function canViewAllTaskAgendaForUser(user) {
  const permissions = user?.permissions || {};
  return (
    Number(user?.id) === 1 ||
    Boolean(user?.has_access_to_all_business_units) ||
    Boolean(permissions.access_settings) ||
    Boolean(permissions["crm.tasks.view_all"])
  );
}

function toDateTimeLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function toDateInputValue(date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatCalendarTime(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatDayHeader(date) {
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
  }).format(date);
}

function formatMonthTitle(date) {
  return new Intl.DateTimeFormat(undefined, {
    month: "long",
    year: "numeric",
  }).format(date);
}

function formatWeekTitle(start, end) {
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  if (sameMonth) {
    return `${new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(start)}`;
  }
  return `${new Intl.DateTimeFormat(undefined, { month: "short", day: "2-digit" }).format(start)} - ${new Intl.DateTimeFormat(
    undefined,
    { month: "short", day: "2-digit", year: "numeric" }
  ).format(end)}`;
}

function startOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function startOfWeek(date) {
  const next = startOfDay(date);
  const day = next.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  next.setDate(next.getDate() + diff);
  return next;
}

function startOfMonth(date) {
  const next = startOfDay(date);
  next.setDate(1);
  return next;
}

function addDays(date, amount) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

function addMonths(date, amount) {
  const next = new Date(date);
  next.setMonth(next.getMonth() + amount);
  return next;
}

function setTime(date, hour, minute = 0) {
  const next = new Date(date);
  next.setHours(hour, minute, 0, 0);
  return next;
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function getTaskDate(task) {
  if (!task?.due_at) return null;
  const date = new Date(task.due_at);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getCalendarRange(anchorDate, view) {
  if (view === "day") {
    const start = startOfDay(anchorDate);
    return { start, end: addDays(start, 1) };
  }

  if (view === "week") {
    const start = startOfWeek(anchorDate);
    return { start, end: addDays(start, 7) };
  }

  const monthStart = startOfMonth(anchorDate);
  const start = startOfWeek(monthStart);
  return { start, end: addDays(start, 42) };
}

function getCalendarTitle(anchorDate, view) {
  if (view === "day") return formatDayHeader(anchorDate);
  if (view === "week") {
    const start = startOfWeek(anchorDate);
    return formatWeekTitle(start, addDays(start, 6));
  }
  return formatMonthTitle(anchorDate);
}

function buildFormFromTask(task) {
  return {
    title: task?.title || "",
    description: task?.description || "",
    task_type: task?.task_type || "follow_up",
    priority: task?.priority || "normal",
    status: task?.status || "open",
    due_at: toDateTimeLocal(task?.due_at),
    reminder_at: toDateTimeLocal(task?.reminder_at),
    conversation_id: task?.conversation_id || "",
    lead_id: task?.lead_id || "",
    contact_customer_id: task?.contact_customer_id || "",
    company_id: task?.company_id || "",
  };
}

function buildCalendarTaskMap(tasks) {
  const map = new Map();
  tasks.forEach((task) => {
    const date = getTaskDate(task);
    if (!date) return;
    const dayKey = toDateInputValue(date);
    const hourKey = `${dayKey}-${date.getHours()}`;
    if (!map.has(dayKey)) map.set(dayKey, []);
    if (!map.has(hourKey)) map.set(hourKey, []);
    map.get(dayKey).push(task);
    map.get(hourKey).push(task);
  });

  map.forEach((items) => {
    items.sort((a, b) => {
      const aDate = getTaskDate(a)?.getTime() || 0;
      const bDate = getTaskDate(b)?.getTime() || 0;
      return aDate - bDate || String(a.title || "").localeCompare(String(b.title || ""));
    });
  });

  return map;
}

export default function Tasks() {
  const copy = useCrmCopy();
  const pageCopy = copy.entities.tasks;
  const calendarCopy = pageCopy.calendar || {};
  const sessionUser = useMemo(() => readStoredCrmUser(), []);
  const canViewAllTaskAgenda = useMemo(() => canViewAllTaskAgendaForUser(sessionUser), [sessionUser]);
  const [searchParams] = useSearchParams();
  const openedTaskIdRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [leads, setLeads] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");
  const [scopeFilter, setScopeFilter] = useState(() => (canViewAllTaskAgenda ? "all" : "mine"));
  const [viewMode, setViewMode] = useState("list");
  const [calendarView, setCalendarView] = useState("week");
  const [calendarDate, setCalendarDate] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const calendarRange = useMemo(
    () => getCalendarRange(calendarDate, calendarView),
    [calendarDate, calendarView]
  );
  const calendarTitle = useMemo(
    () => getCalendarTitle(calendarDate, calendarView),
    [calendarDate, calendarView]
  );
  const calendarTasks = useMemo(() => rows.filter((task) => Boolean(getTaskDate(task))), [rows]);
  const taskMap = useMemo(() => buildCalendarTaskMap(calendarTasks), [calendarTasks]);
  const hours = useMemo(
    () => Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, index) => HOUR_START + index),
    []
  );
  const weekDays = useMemo(() => {
    const start = startOfWeek(calendarDate);
    return Array.from({ length: 7 }, (_, index) => addDays(start, index));
  }, [calendarDate]);
  const monthDays = useMemo(() => {
    const start = calendarRange.start;
    return Array.from({ length: 42 }, (_, index) => addDays(start, index));
  }, [calendarRange.start]);

  const taskTypeOptions = useMemo(
    () =>
      ["follow_up", "call", "email", "whatsapp", "meeting", "todo"].map((type) => ({
        value: type,
        label: copy.taskTypes?.[type] || type,
      })),
    [copy.taskTypes]
  );

  const priorityOptions = useMemo(
    () =>
      ["low", "normal", "high", "urgent"].map((priority) => ({
        value: priority,
        label: copy.taskPriorities?.[priority] || priority,
      })),
    [copy.taskPriorities]
  );

  const statusOptions = useMemo(
    () =>
      ["open", "in_progress", "done", "cancelled"].map((status) => ({
        value: status,
        label: copy.taskStatuses?.[status] || status,
      })),
    [copy.taskStatuses]
  );

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        q: query,
        status: statusFilter === "all" ? "" : statusFilter,
        owner_scope: canViewAllTaskAgenda && scopeFilter === "all" ? "all" : "mine",
        limit: viewMode === "calendar" ? 200 : 100,
        ...(viewMode === "calendar"
          ? {
              due_from: calendarRange.start.toISOString(),
              due_to: calendarRange.end.toISOString(),
            }
          : {}),
      };
      const data = await fetchCrmTasks(params);
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      console.error("[CRM] Tasks load error:", err);
      setError(err?.response?.data?.error || pageCopy.loadError);
    } finally {
      setLoading(false);
    }
  }, [
    calendarRange.end,
    calendarRange.start,
    canViewAllTaskAgenda,
    pageCopy.loadError,
    query,
    scopeFilter,
    statusFilter,
    viewMode,
  ]);

  const loadLookups = useCallback(async () => {
    try {
      const [contactsData, companiesData, leadsData, conversationsData] = await Promise.all([
        fetchCrmContacts({ limit: 200 }),
        fetchCrmCompanies({ limit: 200 }),
        fetchCrmLeads({ limit: 200 }),
        fetchCrmConversations({ limit: 200 }),
      ]);
      setContacts(Array.isArray(contactsData?.rows) ? contactsData.rows : []);
      setCompanies(Array.isArray(companiesData?.rows) ? companiesData.rows : []);
      setLeads(Array.isArray(leadsData?.rows) ? leadsData.rows : []);
      setConversations(Array.isArray(conversationsData?.rows) ? conversationsData.rows : []);
    } catch (err) {
      console.error("[CRM] Task lookups load error:", err);
      setContacts([]);
      setCompanies([]);
      setLeads([]);
      setConversations([]);
    }
  }, []);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (!canViewAllTaskAgenda && scopeFilter !== "mine") {
      setScopeFilter("mine");
    }
  }, [canViewAllTaskAgenda, scopeFilter]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  const openCreate = (dueAt = "") => {
    setEditingTask(null);
    setForm({
      ...emptyForm,
      due_at: dueAt,
    });
    setModalOpen(true);
  };

  const openCreateAt = (date) => {
    openCreate(toDateTimeLocal(date));
  };

  const openEdit = (task) => {
    setEditingTask(task);
    setForm(buildFormFromTask(task));
    setModalOpen(true);
  };

  useEffect(() => {
    const taskId = searchParams.get("task_id");
    if (!taskId || openedTaskIdRef.current === taskId) return;

    openedTaskIdRef.current = taskId;
    setError("");
    fetchCrmTask(taskId)
      .then((task) => {
        if (!task?.id) return;
        setEditingTask(task);
        setForm(buildFormFromTask(task));
        setModalOpen(true);
      })
      .catch((err) => {
        console.error("[CRM] Task deep link load error:", err);
        setError(err?.response?.data?.error || pageCopy.loadError);
      });
  }, [pageCopy.loadError, searchParams]);

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingTask(null);
    setForm(emptyForm);
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = () => ({
    ...form,
    due_at: form.due_at || null,
    reminder_at: form.reminder_at || null,
    conversation_id: form.conversation_id || null,
    lead_id: form.lead_id || null,
    contact_customer_id: form.contact_customer_id || null,
    company_id: form.company_id || null,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      if (editingTask?.id) {
        await updateCrmTask(editingTask.id, buildPayload());
      } else {
        await createCrmTask(buildPayload());
      }
      setModalOpen(false);
      setEditingTask(null);
      setForm(emptyForm);
      await loadTasks();
    } catch (err) {
      console.error("[CRM] Task save error:", err);
      setError(err?.response?.data?.error || pageCopy.saveError);
    } finally {
      setSaving(false);
    }
  };

  const completeTask = async (task) => {
    if (!task?.id) return;
    setSaving(true);
    setError("");
    try {
      await updateCrmTask(task.id, { status: "done" });
      await loadTasks();
    } catch (err) {
      console.error("[CRM] Task complete error:", err);
      setError(err?.response?.data?.error || pageCopy.saveError);
    } finally {
      setSaving(false);
    }
  };

  const goToToday = () => {
    setCalendarDate(new Date());
  };

  const goToPrevious = () => {
    setCalendarDate((current) => {
      if (calendarView === "day") return addDays(current, -1);
      if (calendarView === "week") return addDays(current, -7);
      return addMonths(current, -1);
    });
  };

  const goToNext = () => {
    setCalendarDate((current) => {
      if (calendarView === "day") return addDays(current, 1);
      if (calendarView === "week") return addDays(current, 7);
      return addMonths(current, 1);
    });
  };

  return (
    <main className="crm-page crm-page--compactTable crm-page--tasks">
      <header className="crm-pageHeader">
        <div>
          <p className="crm-eyebrow">{copy.common.crm}</p>
          <h1>{copy.sections.tasks.title}</h1>
        </div>

        <button type="button" className="crm-ghostButton" onClick={() => openCreate()}>
          <FiPlus aria-hidden="true" />
          <span>{pageCopy.newTitle}</span>
        </button>
      </header>

      <section className="crm-panel crm-taskPanel">
        <div className="crm-entityToolbar crm-taskToolbar">
          <label className="crm-searchBox">
            <FiSearch aria-hidden="true" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={pageCopy.searchPlaceholder}
            />
          </label>

          <div className="crm-segmentedControl" aria-label={calendarCopy.viewMode || "Task view"}>
            <button
              type="button"
              className={viewMode === "list" ? "crm-segmentedControlItem--active" : ""}
              onClick={() => setViewMode("list")}
            >
              <FiList aria-hidden="true" />
              <span>{calendarCopy.list || "List"}</span>
            </button>
            <button
              type="button"
              className={viewMode === "calendar" ? "crm-segmentedControlItem--active" : ""}
              onClick={() => setViewMode("calendar")}
            >
              <FiCalendar aria-hidden="true" />
              <span>{calendarCopy.calendar || "Calendar"}</span>
            </button>
          </div>

          <div className="crm-segmentedControl" aria-label={pageCopy.table.status}>
            <button
              type="button"
              className={statusFilter === "open" ? "crm-segmentedControlItem--active" : ""}
              onClick={() => setStatusFilter("open")}
            >
              {pageCopy.filterOpen}
            </button>
            <button
              type="button"
              className={statusFilter === "all" ? "crm-segmentedControlItem--active" : ""}
              onClick={() => setStatusFilter("all")}
            >
              {pageCopy.filterAll}
            </button>
          </div>

          {canViewAllTaskAgenda ? (
            <div className="crm-segmentedControl" aria-label={calendarCopy.scope || "Scope"}>
              <button
                type="button"
                className={scopeFilter === "mine" ? "crm-segmentedControlItem--active" : ""}
                onClick={() => setScopeFilter("mine")}
              >
                {calendarCopy.mine || "Mine"}
              </button>
              <button
                type="button"
                className={scopeFilter === "all" ? "crm-segmentedControlItem--active" : ""}
                onClick={() => setScopeFilter("all")}
              >
                {calendarCopy.all || "All"}
              </button>
            </div>
          ) : null}
        </div>

        {error ? <div className="crm-stateBanner is-error">{error}</div> : null}

        {viewMode === "calendar" ? (
          <TaskCalendar
            calendarCopy={calendarCopy}
            copy={copy}
            pageCopy={pageCopy}
            view={calendarView}
            onViewChange={setCalendarView}
            title={calendarTitle}
            anchorDate={calendarDate}
            onDateChange={setCalendarDate}
            onToday={goToToday}
            onPrevious={goToPrevious}
            onNext={goToNext}
            rows={rows}
            taskMap={taskMap}
            hours={hours}
            weekDays={weekDays}
            monthDays={monthDays}
            loading={loading}
            onCreateAt={openCreateAt}
            onEdit={openEdit}
            onComplete={completeTask}
            saving={saving}
            showOwner={canViewAllTaskAgenda && scopeFilter === "all"}
          />
        ) : (
          <TaskList
            rows={rows}
            loading={loading}
            copy={copy}
            pageCopy={pageCopy}
            saving={saving}
            onEdit={openEdit}
            onComplete={completeTask}
            showOwner={canViewAllTaskAgenda && scopeFilter === "all"}
          />
        )}
      </section>

      {modalOpen ? (
        <div className="crm-modalBackdrop" role="presentation">
          <form className="crm-modal" onSubmit={handleSubmit}>
            <div className="crm-modalHeader">
              <h2>{editingTask ? pageCopy.editTitle : pageCopy.newTitle}</h2>
              <button type="button" className="crm-iconButton" onClick={closeModal} aria-label={copy.common.cancel}>
                <FiX aria-hidden="true" />
              </button>
            </div>

            <div className="crm-formGrid">
              <Field
                label={pageCopy.fields.title}
                value={form.title}
                required
                onChange={(value) => handleFieldChange("title", value)}
              />
              <SelectField
                label={pageCopy.fields.task_type}
                value={form.task_type}
                onChange={(value) => handleFieldChange("task_type", value)}
                options={taskTypeOptions}
              />
              <SelectField
                label={pageCopy.fields.priority}
                value={form.priority}
                onChange={(value) => handleFieldChange("priority", value)}
                options={priorityOptions}
              />
              <SelectField
                label={pageCopy.fields.status}
                value={form.status}
                onChange={(value) => handleFieldChange("status", value)}
                options={statusOptions}
              />
              <Field
                label={pageCopy.fields.due_at}
                value={form.due_at}
                type="datetime-local"
                onChange={(value) => handleFieldChange("due_at", value)}
              />
              <Field
                label={pageCopy.fields.reminder_at}
                value={form.reminder_at}
                type="datetime-local"
                onChange={(value) => handleFieldChange("reminder_at", value)}
              />
              <SelectField
                label={pageCopy.fields.conversation_id}
                value={form.conversation_id}
                onChange={(value) => handleFieldChange("conversation_id", value)}
                options={[
                  { value: "", label: copy.common.noConversation },
                  ...conversations.map((conversation) => ({
                    value: conversation.id,
                    label: conversation.subject,
                  })),
                ]}
              />
              <SelectField
                label={pageCopy.fields.lead_id}
                value={form.lead_id}
                onChange={(value) => handleFieldChange("lead_id", value)}
                options={[
                  { value: "", label: copy.common.noLead },
                  ...leads.map((lead) => ({ value: lead.id, label: lead.title })),
                ]}
              />
              <SelectField
                label={pageCopy.fields.contact_customer_id}
                value={form.contact_customer_id}
                onChange={(value) => handleFieldChange("contact_customer_id", value)}
                options={[
                  { value: "", label: copy.common.noContact },
                  ...contacts.map((contact) => ({ value: contact.id, label: getContactLabel(contact) })),
                ]}
              />
              <SelectField
                label={pageCopy.fields.company_id}
                value={form.company_id}
                onChange={(value) => handleFieldChange("company_id", value)}
                options={[
                  { value: "", label: copy.common.noCompany },
                  ...companies.map((company) => ({ value: company.id, label: company.name })),
                ]}
              />
              <Field
                label={pageCopy.fields.description}
                value={form.description}
                textarea
                onChange={(value) => handleFieldChange("description", value)}
              />
            </div>

            <div className="crm-modalActions">
              <button type="button" className="crm-ghostButton" onClick={closeModal} disabled={saving}>
                {copy.common.cancel}
              </button>
              <button type="submit" className="crm-primaryButton" disabled={saving}>
                {saving ? copy.common.saving : copy.common.save}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </main>
  );
}

function TaskList({ rows, loading, copy, pageCopy, saving, onEdit, onComplete, showOwner }) {
  return (
    <div className="crm-tableShell">
      <table className="crm-table">
        <thead>
          <tr>
            <th>{pageCopy.table.title}</th>
            <th>{pageCopy.table.type}</th>
            <th>{pageCopy.table.relatedTo}</th>
            <th>{pageCopy.table.priority}</th>
            <th>{pageCopy.table.dueAt}</th>
            <th>{pageCopy.table.status}</th>
            <th>{pageCopy.table.actions}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((task) => (
              <tr key={task.id}>
                <td>
                  <strong className="crm-tablePrimary">{task.title}</strong>
                  {showOwner && getTaskOwnerLabel(task) ? (
                    <span className="crm-tableSecondary">{`Owner: ${getTaskOwnerLabel(task)}`}</span>
                  ) : null}
                  {task.description ? <span className="crm-tableSecondary">{task.description}</span> : null}
                </td>
                <td>
                  <span className={`crm-statusPill crm-statusPill--${task.task_type || "follow_up"}`}>
                    {copy.taskTypes?.[task.task_type] || task.task_type}
                  </span>
                </td>
                <td>{getRelatedLabel(task, copy)}</td>
                <td>
                  <span className={`crm-statusPill crm-statusPill--${task.priority || "normal"}`}>
                    {copy.taskPriorities?.[task.priority] || task.priority}
                  </span>
                </td>
                <td>{formatDateTime(task.due_at)}</td>
                <td>
                  <span className={`crm-statusPill crm-statusPill--${task.status || "open"}`}>
                    {copy.taskStatuses?.[task.status] || task.status}
                  </span>
                </td>
                <td>
                  <div className="crm-rowActions">
                    <button
                      type="button"
                      className="crm-iconButton"
                      onClick={() => onEdit(task)}
                      title={copy.common.edit}
                      aria-label={copy.common.edit}
                    >
                      <FiEdit2 aria-hidden="true" />
                    </button>
                    {task.status !== "done" && task.status !== "cancelled" ? (
                      <button
                        type="button"
                        className="crm-iconButton"
                        onClick={() => onComplete(task)}
                        title={pageCopy.complete}
                        aria-label={pageCopy.complete}
                        disabled={saving}
                      >
                        <FiCheckSquare aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7">
                <div className="crm-tableEmpty">{loading ? copy.common.loading : pageCopy.empty}</div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function TaskCalendar({
  calendarCopy,
  copy,
  pageCopy,
  view,
  onViewChange,
  title,
  anchorDate,
  onDateChange,
  onToday,
  onPrevious,
  onNext,
  rows,
  taskMap,
  hours,
  weekDays,
  monthDays,
  loading,
  onCreateAt,
  onEdit,
  onComplete,
  saving,
  showOwner,
}) {
  const [overflowPopover, setOverflowPopover] = useState(null);
  const openOverflowPopover = (event, titleText, tasks) => {
    event.stopPropagation();
    setOverflowPopover({
      title: titleText,
      tasks: Array.isArray(tasks) ? tasks : [],
    });
  };
  const closeOverflowPopover = () => setOverflowPopover(null);

  return (
    <div className="crm-calendarShell">
      <div className="crm-calendarToolbar">
        <div className="crm-calendarNav">
          <button type="button" className="crm-iconButton" onClick={onPrevious} aria-label={calendarCopy.previous || "Previous"}>
            <FiChevronLeft aria-hidden="true" />
          </button>
          <button type="button" className="crm-ghostButton" onClick={onToday}>
            {calendarCopy.today || "Today"}
          </button>
          <button type="button" className="crm-iconButton" onClick={onNext} aria-label={calendarCopy.next || "Next"}>
            <FiChevronRight aria-hidden="true" />
          </button>
          <input
            className="crm-calendarDateInput"
            type="date"
            value={toDateInputValue(anchorDate)}
            onChange={(event) => {
              if (event.target.value) onDateChange(new Date(`${event.target.value}T12:00:00`));
            }}
            aria-label={calendarCopy.jumpToDate || "Jump to date"}
          />
        </div>

        <div className="crm-calendarTitle">
          <FiCalendar aria-hidden="true" />
          <strong>{title}</strong>
          <span>{calendarCopy.summary ? calendarCopy.summary(rows.length) : `${rows.length} tasks`}</span>
        </div>

        <div className="crm-segmentedControl" aria-label={calendarCopy.view || "Calendar view"}>
          {["day", "week", "month"].map((mode) => (
            <button
              key={mode}
              type="button"
              className={view === mode ? "crm-segmentedControlItem--active" : ""}
              onClick={() => onViewChange(mode)}
            >
              {calendarCopy[mode] || mode}
            </button>
          ))}
        </div>
      </div>

      <div className="crm-calendarHint">
        <FiClock aria-hidden="true" />
        <span>{calendarCopy.clickHint || "Click a date or hour to create a task."}</span>
      </div>

      {view === "month" ? (
        <MonthCalendar
          calendarCopy={calendarCopy}
          copy={copy}
          pageCopy={pageCopy}
          anchorDate={anchorDate}
          days={monthDays}
          taskMap={taskMap}
          loading={loading}
          onCreateAt={onCreateAt}
          onEdit={onEdit}
          onComplete={onComplete}
          saving={saving}
          showOwner={showOwner}
          onShowMore={openOverflowPopover}
        />
      ) : (
        <TimeGridCalendar
          calendarCopy={calendarCopy}
          copy={copy}
          pageCopy={pageCopy}
          days={view === "day" ? [startOfDay(anchorDate)] : weekDays}
          taskMap={taskMap}
          hours={hours}
          loading={loading}
          onCreateAt={onCreateAt}
          onEdit={onEdit}
          onComplete={onComplete}
          saving={saving}
          showOwner={showOwner}
          compact={view === "day"}
          onShowMore={openOverflowPopover}
        />
      )}

      {overflowPopover ? (
        <CalendarOverflowPopover
          calendarCopy={calendarCopy}
          copy={copy}
          pageCopy={pageCopy}
          title={overflowPopover.title}
          tasks={overflowPopover.tasks}
          onClose={closeOverflowPopover}
          onEdit={onEdit}
          onComplete={onComplete}
          saving={saving}
          showOwner={showOwner}
        />
      ) : null}
    </div>
  );
}

function TimeGridCalendar({
  calendarCopy,
  copy,
  pageCopy,
  days,
  taskMap,
  hours,
  loading,
  onCreateAt,
  onEdit,
  onComplete,
  saving,
  compact,
  showOwner,
  onShowMore,
}) {
  return (
    <div className={compact ? "crm-timeCalendar crm-timeCalendar--day" : "crm-timeCalendar"}>
      <div className="crm-timeCalendarHeader">
        <div className="crm-timeCalendarCorner" />
        {days.map((day) => (
          <div key={day.toISOString()} className={isSameDay(day, new Date()) ? "is-today" : ""}>
            <span>{new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(day)}</span>
            <strong>{day.getDate()}</strong>
          </div>
        ))}
      </div>

      <div className="crm-timeCalendarBody">
        {hours.map((hour) => (
          <React.Fragment key={hour}>
            <div className="crm-timeCalendarHour">{`${String(hour).padStart(2, "0")}:00`}</div>
            {days.map((day) => {
              const slotDate = setTime(day, hour);
              const key = `${toDateInputValue(day)}-${hour}`;
              const tasks = taskMap.get(key) || [];
              const visibleLimit = compact ? 3 : 2;
              const visibleTasks = tasks.slice(0, visibleLimit);
              const hiddenTasks = tasks.slice(visibleTasks.length);
              const hiddenCount = hiddenTasks.length;
              return (
                <div
                  key={`${day.toISOString()}-${hour}`}
                  className={isSameDay(day, new Date()) ? "crm-timeSlot is-today" : "crm-timeSlot"}
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    if (event.target === event.currentTarget) onCreateAt(slotDate);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") onCreateAt(slotDate);
                  }}
                  aria-label={`${calendarCopy.newAt || "New task at"} ${formatDateTime(slotDate)}`}
                >
                  <div className="crm-timeSlotTasks">
                    {visibleTasks.map((task) => (
                      <TaskCalendarCard
                        key={task.id}
                        task={task}
                        copy={copy}
                        pageCopy={pageCopy}
                        onEdit={onEdit}
                        onComplete={onComplete}
                        saving={saving}
                        showOwner={showOwner}
                        compact
                      />
                    ))}
                    {hiddenCount ? (
                      <button
                        type="button"
                        className="crm-calendarOverflowButton"
                        onClick={(event) =>
                          onShowMore(
                            event,
                            `${formatDayHeader(day)} · ${String(hour).padStart(2, "0")}:00`,
                            tasks
                          )
                        }
                      >
                        {calendarCopy.more ? calendarCopy.more(hiddenCount) : `+${hiddenCount} more`}
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      {!loading && !Array.from(taskMap.values()).some((items) => items.length) ? (
        <div className="crm-calendarEmpty">{calendarCopy.empty || pageCopy.empty}</div>
      ) : null}
    </div>
  );
}

function MonthCalendar({
  calendarCopy,
  copy,
  pageCopy,
  anchorDate,
  days,
  taskMap,
  loading,
  onCreateAt,
  onEdit,
  onComplete,
  saving,
  showOwner,
  onShowMore,
}) {
  const weekLabels = days.slice(0, 7);

  return (
    <div className="crm-monthCalendar">
      <div className="crm-monthCalendarWeekdays">
        {weekLabels.map((day) => (
          <span key={day.toISOString()}>{new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(day)}</span>
        ))}
      </div>

      <div className="crm-monthCalendarGrid">
        {days.map((day) => {
          const key = toDateInputValue(day);
          const tasks = taskMap.get(key) || [];
          const visibleTasks = tasks.slice(0, 4);
          const hiddenCount = Math.max(tasks.length - visibleTasks.length, 0);
          const isOutsideMonth = day.getMonth() !== anchorDate.getMonth();
          return (
            <div
              key={day.toISOString()}
              className={[
                "crm-monthDay",
                isOutsideMonth ? "is-muted" : "",
                isSameDay(day, new Date()) ? "is-today" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              role="button"
              tabIndex={0}
              onClick={(event) => {
                if (event.target === event.currentTarget) onCreateAt(setTime(day, 9));
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") onCreateAt(setTime(day, 9));
              }}
              aria-label={`${calendarCopy.newOn || "New task on"} ${formatDayHeader(day)}`}
            >
              <div className="crm-monthDayHeader">
                <strong>{day.getDate()}</strong>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onCreateAt(setTime(day, 9));
                  }}
                  aria-label={`${calendarCopy.newOn || "New task on"} ${formatDayHeader(day)}`}
                >
                  <FiPlus aria-hidden="true" />
                </button>
              </div>
              <div className="crm-monthDayTasks">
                {visibleTasks.map((task) => (
                  <TaskCalendarCard
                    key={task.id}
                    task={task}
                    copy={copy}
                    pageCopy={pageCopy}
                    onEdit={onEdit}
                    onComplete={onComplete}
                    saving={saving}
                    showOwner={showOwner}
                    compact
                  />
                ))}
                {hiddenCount ? (
                  <button
                    type="button"
                    className="crm-monthOverflow"
                    onClick={(event) => onShowMore(event, formatDayHeader(day), tasks)}
                  >
                    {calendarCopy.more ? calendarCopy.more(hiddenCount) : `+${hiddenCount} more`}
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {!loading && !Array.from(taskMap.values()).some((items) => items.length) ? (
        <div className="crm-calendarEmpty">{calendarCopy.empty || pageCopy.empty}</div>
      ) : null}
    </div>
  );
}

function CalendarOverflowPopover({
  calendarCopy,
  copy,
  pageCopy,
  title,
  tasks,
  onClose,
  onEdit,
  onComplete,
  saving,
  showOwner,
}) {
  return (
    <div className="crm-calendarPopoverLayer" role="presentation" onClick={onClose}>
      <section
        className="crm-calendarOverflowPopover"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <strong>{title}</strong>
            <span>{calendarCopy.summary ? calendarCopy.summary(tasks.length) : `${tasks.length} tasks`}</span>
          </div>
          <button type="button" className="crm-iconButton" onClick={onClose} aria-label={copy.common.close || "Close"}>
            <FiX aria-hidden="true" />
          </button>
        </header>
        <div className="crm-calendarOverflowList">
          {tasks.map((task) => {
            const dueDate = getTaskDate(task);
            const ownerLabel = getTaskOwnerLabel(task);
            return (
              <article key={task.id} className={`crm-calendarOverflowRow crm-calendarOverflowRow--${task.priority || "normal"}`}>
                <button type="button" onClick={() => onEdit(task)}>
                  <span>{dueDate ? formatCalendarTime(dueDate) : pageCopy.dueLabel}</span>
                  <strong>{task.title}</strong>
                  <em>{getRelatedLabel(task, copy)}</em>
                  {showOwner && ownerLabel ? <small>{`Owner: ${ownerLabel}`}</small> : null}
                </button>
                {task.status !== "done" && task.status !== "cancelled" ? (
                  <button
                    type="button"
                    className="crm-calendarTaskDone"
                    onClick={() => onComplete(task)}
                    disabled={saving}
                    title={pageCopy.complete}
                    aria-label={pageCopy.complete}
                  >
                    <FiCheckSquare aria-hidden="true" />
                  </button>
                ) : null}
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function TaskCalendarCard({ task, copy, pageCopy, onEdit, onComplete, saving, compact = false, showOwner = false }) {
  const dueDate = getTaskDate(task);
  const ownerLabel = getTaskOwnerLabel(task);
  const className = [
    "crm-calendarTask",
    `crm-calendarTask--${task.priority || "normal"}`,
    compact ? "crm-calendarTask--compact" : "",
    showOwner && ownerLabel ? "crm-calendarTask--hasOwner" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <article className={className}>
      <button type="button" className="crm-calendarTaskMain" onClick={() => onEdit(task)}>
        <span className="crm-calendarTaskTime">{dueDate ? formatCalendarTime(dueDate) : pageCopy.dueLabel}</span>
        <strong className="crm-calendarTaskTitle">{task.title}</strong>
        {!compact ? <em className="crm-calendarTaskRelated">{getRelatedLabel(task, copy)}</em> : null}
        {showOwner && ownerLabel ? <em className="crm-calendarTaskOwner">{`Owner: ${ownerLabel}`}</em> : null}
      </button>
      {task.status !== "done" && task.status !== "cancelled" ? (
        <button
          type="button"
          className="crm-calendarTaskDone"
          onClick={() => onComplete(task)}
          disabled={saving}
          title={pageCopy.complete}
          aria-label={pageCopy.complete}
        >
          <FiCheckSquare aria-hidden="true" />
        </button>
      ) : null}
    </article>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  textarea = false,
  required = false,
}) {
  return (
    <label className={`crm-field ${textarea ? "crm-field--wide" : ""}`}>
      <span>{label}</span>
      {textarea ? (
        <textarea value={value} onChange={(event) => onChange(event.target.value)} rows={4} required={required} />
      ) : (
        <input type={type} value={value} onChange={(event) => onChange(event.target.value)} required={required} />
      )}
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="crm-field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
